import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Divider from 'LuxKit/Divider'
import MarkdownRender from 'LuxKit/MarkdownRender'
import styled from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import FakeInput from 'components/Common/Form/Input/FakeInput'
import useToggle from 'hooks/useToggle'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { checkTourExperienceTimeSlot } from 'lib/tours/tourUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { EmptyArray } from 'lib/array/arrayUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorTourExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { shouldShowFreeCancellationForTourExperience } from 'lib/tours/tourExperienceFreeCancellation'

const DividerExtend = styled(Divider)`
  margin: ${rem(16)} 0;
`

const BodyTextBlockExtend = styled(BodyTextBlock)`
  margin: ${rem(12)} 0;
`

const TimeSlotWrapper = styled.div`
  width: 100%;
  ${mediaQueryUp.tablet} {
    width: ${rem(300)};
  }
`

interface Props {
  tourExperience: App.Tours.TourExperience
  title: string
  date?: Date
  day?: number
  countOccupants?: number
  isCheckout?: boolean
  isForm?: boolean
  tourExperienceItems?: Array<App.Checkout.TourV2ExperienceItem>
  onSubmit?: (tourExperience: App.Tours.TourExperience, daySelected: number, timeSlot?: string) => void
  offerId?: string
  offerName?: string
}

function TourExperienceModal(props: Props) {
  const {
    tourExperience,
    title,
    date,
    day,
    tourExperienceItems = EmptyArray,
    countOccupants = 0,
    onSubmit,
    isCheckout = false,
  } = props

  const intl = useIntl()
  const modalState = useModalElementContext<unknown>()
  const triggerRef = useRef<HTMLLabelElement | null>(null)
  const [open, toggleDropdown, , closeDropdown] = useToggle()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('')

  const selectTimeSlot = useCallback((timeSlot: string) => {
    return () => {
      setSelectedTimeSlot(timeSlot)
      closeDropdown()
    }
  }, [closeDropdown])

  const submitTimeSlot = useCallback(() => {
    if (onSubmit) {
      onSubmit(tourExperience, day ?? 0, selectedTimeSlot)
      modalState.resolve(true)
    }
  }, [onSubmit, tourExperience, day, selectedTimeSlot, modalState])

  const totalPrice = useMemo(() => (tourExperience?.price ?? 0) * (countOccupants ?? 0), [tourExperience.price, countOccupants])
  const loyaltyCalculationRequests = [generateLoyaltyPointsCalculatorTourExperienceOptions(tourExperience)]
  const suppressFreeCancellation = useMemo(() => shouldShowFreeCancellationForTourExperience(tourExperience), [tourExperience])

  const availabilityMessage = useMemo(() => {
    if (tourExperience.price > 0) {
      return intl.formatMessage({
        id: 'ZHCfigRKDgbNMsm07S1L',
        defaultMessage: 'This will be available to purchase when booking your tour.',
        description: 'Message explaining paid experience can be purchased during booking',
      })
    }
    return intl.formatMessage({
      id: '077JBYthRwoLeNUt-DBk',
      defaultMessage: 'This will be available to add on when booking your tour.',
      description: 'Message explaining free experience can be added during booking',
    })
  }, [tourExperience.price, intl])

  return <ModalBase>
    <ModalHeader title={tourExperience.name} subtitle={title} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">{intl.formatMessage({
            id: 'OBszUCV67r4b7zLkaQRJ',
            defaultMessage: 'Overview',
            description: 'Heading for tour experience overview section on the tour page',
          })}
          </Heading>
          <MarkdownRender fontSize="medium" content={tourExperience.description ?? ''} />
          {onSubmit && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            {(suppressFreeCancellation && !tourExperience.nonRefundable) && <BodyTextBlock
              variant="medium"
              colour="highlight-secondary"
              weight="bold"
              endIcon={<Tooltip
                placement="right"
                description={intl.formatMessage({
                  id: 'aiLs-9x-eGAYmjBJETbp',
                  defaultMessage: 'Free Cancellation until 60 days of tour departure',
                  description: 'Tooltip explaining free cancellation policy for tour experiences',
                })}
              ><LineInfoCircleIcon colour="neutral-two" />
              </Tooltip>}
            >
              {intl.formatMessage({
                id: 'RQorowXWpmfnHL3xNfSC',
                defaultMessage: 'Free cancellation',
                description: 'Label for free cancellation benefit in the cancellation or refund flow',
              })}
            </BodyTextBlock>}
            <BodyTextBlockExtend variant="medium" startIcon={<LineClockFiveIcon />}>{intl.formatMessage(
              {
                id: 'uwq39n0jSWU63OYTJfSZ',
                defaultMessage: 'Duration {duration}',
                description: 'Label showing experience duration on the experience offer or booking page',
              },
              { duration: tourExperience.durationFormatted },
            )}
            </BodyTextBlockExtend>
          </Group>}
          {!onSubmit && <>
            {(suppressFreeCancellation && !tourExperience.nonRefundable) && <BodyTextBlock
              variant="medium"
              colour="highlight-secondary"
              weight="bold"
              endIcon={<Tooltip
                placement="right"
                description={intl.formatMessage({
                  id: 'aiLs-9x-eGAYmjBJETbp',
                  defaultMessage: 'Free Cancellation until 60 days of tour departure',
                  description: 'Tooltip explaining free cancellation policy for tour experiences',
                })}
              ><LineInfoCircleIcon colour="neutral-two" />
              </Tooltip>}
            >
              {intl.formatMessage({
                id: 'RQorowXWpmfnHL3xNfSC',
                defaultMessage: 'Free cancellation',
                description: 'Label for free cancellation benefit in the cancellation or refund flow',
              })}
            </BodyTextBlock>}
            {(!!tourExperience.timeSlots.length || tourExperience.durationFormatted) && <BodyTextBlockExtend variant="medium" startIcon={<LineClockFiveIcon />}>
              {intl.formatMessage(
                {
                  id: 'IsqkHfP7k-397WDsm2lJ',
                  defaultMessage: '{timeSlots} ({duration})',
                  description: 'Tour experience time slots with formatted duration',
                },
                {
                  timeSlots: tourExperience.timeSlots.join(formatMiddleDotSeparator(intl)),
                  duration: tourExperience.durationFormatted,
                },
              )}
            </BodyTextBlockExtend>}
          </>}
          {onSubmit && <TimeSlotWrapper>
            <FakeInput
              required
              label={intl.formatMessage({
                id: 'ozwQdiP9fz0rKdG0Av8s',
                defaultMessage: 'Select a preferred time',
                description: 'Form field label for time slot selection dropdown in the tours section',
              })}
              ref={triggerRef}
              noValidationSpacing
              value={selectedTimeSlot}
              onClick={toggleDropdown}
              endIcon={<DropdownChevron open={open} />}
            />
            <DropdownList
              title=""
              anchorRef={triggerRef}
              triggerRef={triggerRef}
              open={open}
              onClose={closeDropdown}
              size="fill-anchor"
            >
              {tourExperience.timeSlots.map((timeSlot, index) =>
                (<ClickableListItem
                  key={index}
                  disabled={date && !checkTourExperienceTimeSlot(tourExperienceItems, [timeSlot], date)}
                  selected={timeSlot === selectedTimeSlot}
                  onClick={selectTimeSlot(timeSlot)}
                  title={timeSlot}
                />),
              )}
            </DropdownList>
          </TimeSlotWrapper>}
          {!onSubmit &&
            <div>
              <LoyaltyPointsEarn calculationRequests={loyaltyCalculationRequests} calculationType="estimate" />
              {totalPrice > 0 && <PriceRowPrice
                size="M"
                price={tourExperience.price}
                saleUnit="person"
                signDisplay="always"
              />}
              {totalPrice === 0 && <BodyText variant="medium" weight="bold">{intl.formatMessage({
                id: 'zXZGIDaTw4caG8tzOgpb',
                defaultMessage: 'Included',
                description: 'Label indicating the experience is included in the tour price',
              })}
              </BodyText>}
              {countOccupants > 1 && (totalPrice > 0) && <Caption variant="medium" colour="neutral-three">
                <FormattedMessage
                  id="kFSIK10see_g3nmGY7gv"
                  defaultMessage="{total} total for all travellers"
                  description="Caption showing total price for all travellers in the tours section"
                  values={{ total: <FormatCurrency value={totalPrice} format="roundedDollar" /> }}
                />
              </Caption>}
            </div>}
          {!isCheckout && <>
            <DividerExtend kind="primary" />
            <BodyText variant="small">{availabilityMessage}</BodyText>
          </>}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    {onSubmit && <ModalFooter>
      <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="space-between" tabletVerticalAlign="center" gap={16}>
        <div>
          <PriceRowPrice
            size="M"
            price={tourExperience.price}
            saleUnit="person"
            signDisplay="always"
          />
          {countOccupants > 1 && <Caption variant="medium" colour="neutral-three">
            <FormattedMessage
              id="kFSIK10see_g3nmGY7gv"
              defaultMessage="{total} total for all travellers"
              description="Caption showing total price for all travellers in the tours section"
              values={{ total: <FormatCurrency value={totalPrice} format="roundedDollar" /> }}
            />
          </Caption>}
        </div>
        <TextButton kind="primary" disabled={!selectedTimeSlot} onClick={submitTimeSlot}>{intl.formatMessage({
          id: 'dwGv1RFnc89ZqbTHDkP6',
          defaultMessage: 'Add',
          description: 'Button text to add experience to booking in the booking flow',
        })}
        </TextButton>
      </Group>
    </ModalFooter>}
  </ModalBase>
}

export default TourExperienceModal
